package fr.frux.core.designsystem.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.resources.Res
import fr.frux.core.designsystem.resources.ds_note_counter
import org.jetbrains.compose.resources.stringResource

private const val NOTE_DISABLED_ALPHA = 0.38f

/**
 * Note libre multiligne : libellé au-dessus, saisie tronquée à [maxLength], compteur « 121 / 2000 »
 * aligné à droite sous le champ quand [showCounter].
 */
@Composable
fun FruxNoteField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    placeholder: String,
    maxLength: Int,
    showCounter: Boolean,
    modifier: Modifier = Modifier,
    minLines: Int = 2,
    enabled: Boolean = true,
    labelStyle: TextStyle = MaterialTheme.typography.titleMedium,
) {
    val shown = value.clampTo(maxLength)
    Column(
        modifier = modifier.fillMaxWidth().alpha(if (enabled) 1f else NOTE_DISABLED_ALPHA),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            text = label,
            style = labelStyle,
            color = MaterialTheme.colorScheme.onSurface,
        )
        Surface(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline),
        ) {
            BasicTextField(
                value = shown,
                onValueChange = { onValueChange(it.clampTo(maxLength)) },
                modifier = Modifier.fillMaxWidth().semantics { contentDescription = label },
                enabled = enabled,
                minLines = minLines,
                textStyle =
                    MaterialTheme.typography.bodyLarge.copy(color = MaterialTheme.colorScheme.onSurface),
                cursorBrush = SolidColor(MaterialTheme.colorScheme.primary),
                decorationBox = { inner ->
                    Box(Modifier.padding(16.dp)) {
                        if (shown.isEmpty()) {
                            Text(
                                text = placeholder,
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                        inner()
                    }
                },
            )
        }
        if (showCounter) {
            Text(
                text = stringResource(Res.string.ds_note_counter, shown.length, maxLength),
                modifier = Modifier.fillMaxWidth(),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                textAlign = TextAlign.End,
            )
        }
    }
}

/** Coupe à [max] unités UTF-16 sans scinder une paire de surrogates. */
private fun String.clampTo(max: Int): String {
    if (length <= max) return this
    val end = if (max > 0 && this[max - 1].isHighSurrogate()) max - 1 else max
    return substring(0, end)
}
